import { useEffect, useRef, useState } from "react";
import type { LeadTimeUnit, LeadTimeValue } from "@/lib/types";
import { t } from "@/lib/strings";

interface Props {
  value: LeadTimeValue | null;
  /** Board goal date (ISO yyyy-mm-dd) the lead time counts back from. */
  goalDate: string | null;
  rowH: number;
  onPatch?: (value: LeadTimeValue | null) => void;
}

const UNIT_LABELS: Record<LeadTimeUnit, string> = {
  days: t.leadUnitDays,
  weeks: t.leadUnitWeeks,
  months: t.leadUnitMonths,
};

/** goalDate − duration → the derived deadline (local Date), or null. */
function deriveDeadline(goalDate: string | null, lt: LeadTimeValue | null): Date | null {
  if (!goalDate || !lt) return null;
  const d = new Date(`${goalDate}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  if (lt.unit === "days") d.setDate(d.getDate() - lt.value);
  else if (lt.unit === "weeks") d.setDate(d.getDate() - lt.value * 7);
  else d.setMonth(d.getMonth() - lt.value);
  return d;
}

function fmt(d: Date): string {
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}.${mm}.${d.getFullYear()}`;
}

function daysUntil(d: Date): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(d);
  target.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / 86_400_000);
}

/** Urgency colour for the derived deadline: red overdue/imminent, amber soon. */
function urgencyColor(d: Date | null): string {
  if (!d) return "var(--ink-2)";
  const n = daysUntil(d);
  if (n < 0) return "#b42318"; // overdue
  if (n <= 2) return "#b42318"; // imminent
  if (n <= 7) return "#9a6a2f"; // soon (amber)
  return "var(--ink-2)";
}

export function LeadTimeCell({ value, goalDate, rowH, onPatch }: Props) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState<string>(value ? String(value.value) : "");
  const [unit, setUnit] = useState<LeadTimeUnit>(value?.unit ?? "weeks");
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    // Only re-seed from props while the editor is CLOSED — re-seeding mid-edit
    // (the parent rebuilds `value` on every board refetch/poll) would wipe the
    // amount the user is typing. Mirrors the other editable cells' !editing guard.
    if (open) return;
    setAmount(value ? String(value.value) : "");
    setUnit(value?.unit ?? "weeks");
  }, [value, open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const deadline = deriveDeadline(goalDate, value);

  function commit() {
    if (!onPatch) return;
    const n = Number(amount.replace(",", "."));
    if (!amount.trim() || Number.isNaN(n) || n < 0) onPatch(null);
    else onPatch({ value: n, unit });
    setOpen(false);
  }

  return (
    <div
      style={{
        position: "relative",
        height: rowH,
        display: "flex",
        alignItems: "center",
        padding: "0 10px",
        fontSize: 13,
        cursor: onPatch ? "pointer" : "default",
      }}
      onClick={() => onPatch && setOpen(true)}
    >
      {value ? (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0 }}>
          <span style={{ color: "var(--ink-2)", whiteSpace: "nowrap" }}>
            {value.value} {UNIT_LABELS[value.unit]}
          </span>
          {deadline ? (
            <span style={{ color: urgencyColor(deadline), fontWeight: 500, whiteSpace: "nowrap" }}>
              → {fmt(deadline)}
            </span>
          ) : (
            <span style={{ color: "var(--ink-4)", fontSize: 11.5, whiteSpace: "nowrap" }}>
              {t.leadNoGoalDate}
            </span>
          )}
        </span>
      ) : (
        <span style={{ color: "var(--ink-4)" }}>{onPatch ? t.leadSet : "—"}</span>
      )}

      {open && onPatch ? (
        <>
          <div
            onClick={(e) => {
              e.stopPropagation();
              commit();
            }}
            style={{ position: "fixed", inset: 0, zIndex: 200 }}
          />
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "absolute",
              top: "calc(100% - 4px)",
              left: 6,
              zIndex: 201,
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: 8,
              background: "var(--surface-elev)",
              border: "1px solid var(--hairline-strong)",
              borderRadius: 9,
              boxShadow: "0 10px 30px rgba(13,62,99,0.16)",
            }}
          >
            <input
              ref={inputRef}
              type="number"
              min={0}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") commit();
                if (e.key === "Escape") setOpen(false);
              }}
              style={{
                width: 64,
                height: 32,
                padding: "0 8px",
                fontSize: 13,
                borderRadius: 7,
                border: "1px solid var(--hairline-strong)",
                background: "var(--surface)",
                color: "var(--ink-1)",
                outline: "none",
              }}
            />
            <select
              value={unit}
              onChange={(e) => setUnit(e.target.value as LeadTimeUnit)}
              style={{
                height: 32,
                padding: "0 6px",
                fontSize: 13,
                borderRadius: 7,
                border: "1px solid var(--hairline-strong)",
                background: "var(--surface)",
                color: "var(--ink-1)",
                outline: "none",
              }}
            >
              <option value="days">{t.leadUnitDays}</option>
              <option value="weeks">{t.leadUnitWeeks}</option>
              <option value="months">{t.leadUnitMonths}</option>
            </select>
          </div>
        </>
      ) : null}
    </div>
  );
}
